import { FC, useEffect, useState } from 'react';
import { Button } from 'components/Button/Button';
import { getQuestionOptionLetter, getStepValue } from './utils';
import { Loader } from 'components/Loader/Loader';
import { LOADER_SLIDES_TEXT, LOADING_SLIDE_TIMEOUT_MS } from 'constants/index';
import { useFetch } from 'pages/Quiz/hooks/useFetch';
import { ErrorBlock } from 'components/Error/Error';
import { ResultsLoader } from '../ResultsLoader/ResultsLoader';
import { Scrollbar } from 'components/Scrollbar/Scrollbar';
import { TriangleIcon } from 'assets/svg';
import type { IQuestionnaireProps, TAnswer, TQuestion } from './models';

import { ArrowIcon } from 'assets/svg';
import * as S from './Questionnaire.styled';
import image1 from './img/1.webp';
import image2 from './img/2.webp';
import image3 from './img/3.webp';
import image4 from './img/4.webp';
import image5 from './img/5.webp';
import image6 from './img/6.webp';
import image7 from './img/7.webp';
import image8 from './img/8.webp';
import image9 from './img/9.webp';
import image10 from './img/10.webp';
import image11 from './img/11.webp';
import image12 from './img/12.webp';
import image13 from './img/13.webp';
import image14 from './img/14.webp';

export const Questionnaire: FC<IQuestionnaireProps> = ({ onSaveSummary, sessionKey }) => {
  const [allQuestions, setAllQuestions] = useState<TQuestion[] | []>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [extraContinue, setExtraContinue] = useState(2);
  const [answers, setAnswers] = useState<TAnswer[]>([]);
  const [areAllAwswersSet, setAreAllAwswersSet] = useState(false);
  const [isSubmitLoadingTimerActive, setIsSubmitLoadingTimerActive] =
    useState(false);
  const [summary, setSummary] = useState('');

  const [imagesNumbers, setImagesNumbers] = useState([1,2,3,4,5,6,7,8,9,10,11,12,13,14]);
  const [imageToShow, setImageToShow] = useState(image1);
  const {
    questions,
    isLoadingQuestions,
    isQuestionsError,

    additionalQuestion,
    isAdditionalQuestionsError,
    isLoadingAdditionalQuestions,
    fetchMoreQuestions,

    submitAnswersMutation,
    isLoadingSubmitAnswers,
    isSubmitAnswersError,
  } = useFetch({ setSummary, sessionKey });

  useEffect(() => {
    if (summary && !isSubmitLoadingTimerActive) {
      onSaveSummary(summary);
    }
  }, [summary, isSubmitLoadingTimerActive]);

  useEffect(() => {
    setImageToShow(getPic());
  }, [currentIndex]);

  useEffect(() => {
    if (isLoadingSubmitAnswers) {
      setIsSubmitLoadingTimerActive(true);
      setTimeout(() => {
        setIsSubmitLoadingTimerActive(false);
      }, LOADING_SLIDE_TIMEOUT_MS * LOADER_SLIDES_TEXT.length);
    }
  }, [isLoadingSubmitAnswers]);

  // set additional questions to all
  useEffect(() => {
    if (additionalQuestion) {
      setAreAllAwswersSet(false);
      setCurrentIndex((prevIndex) => prevIndex + 1);
      setAllQuestions((prev) => [...prev, ...additionalQuestion]);
    }
  }, [additionalQuestion]);

  // load init questions
  useEffect(() => {
    if (questions && allQuestions.length === 0) {
      setAllQuestions(questions);
    }
  }, [questions]);

  const handleOptionClick = (questionId: number, optionId: number) => {
    setAnswers((prevAnswers) => {
      const existingAnswer = prevAnswers.find(
        (answer) => answer.questionId === questionId,
      );

      if (existingAnswer) {
        // Update existing answer
        return prevAnswers.map((answer) =>
          answer.questionId === questionId ? { ...answer, optionId } : answer,
        );
      } else {
        // Add new answer
        return [...prevAnswers, { questionId, optionId }];
      }
    });
  };

  const handleNext = () => {
    if (currentIndex < allQuestions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleSubmit = () => {
    setAreAllAwswersSet(true);
    if (extraContinue === 0) {
      onSendResults();
    }
  };

  const stepsContent = (
    <S.QuestionStep>
      <S.Label>Question </S.Label>
      {getStepValue(currentIndex + 1)}/{getStepValue(allQuestions.length)}
    </S.QuestionStep>
  );

  const onLoadMoreQuestions = () => {
    setExtraContinue(extraContinue - 1);
    fetchMoreQuestions(sessionKey as any);
  };

  const onSendResults = () => {
    submitAnswersMutation(answers);
  };

  const getPic = () => {
    let randomValue = imagesNumbers[Math.floor(Math.random()*imagesNumbers.length)];
    setImagesNumbers(imagesNumbers.filter(num => num !== randomValue));
    switch (randomValue) {
      case 1:
        return image1;
      case 2:
        return image2;
      case 3:
        return image3;
      case 4:
        return image4;
      case 5:
        return image5;
      case 6:
        return image6;
      case 7:
        return image7;
      case 8:
        return image8;
      case 9:
        return image9;
      case 10:
        return image10;
      case 11:
        return image11;
      case 12:
        return image12;
      case 13:
        return image13;
      case 14:
        return image14;
      default:
        return image1;
    }
  }

  const renderBottomButton = () => {
    if (areAllAwswersSet ) {
      return null;
    } else if (currentIndex < allQuestions.length - 1) {
      return (
        <S.BottomButton onClick={handleNext} disabled={!answers[currentIndex]}>
          <S.ButtonText>Next</S.ButtonText>
          <S.ButtonContent>
            <TriangleIcon />
            <TriangleIcon />
            <TriangleIcon />
          </S.ButtonContent>
        </S.BottomButton>
      );
    } else {
      return (
        <>
          <S.SubmitButton onClick={handleSubmit} disabled={!answers[currentIndex]}>
            Submit
          </S.SubmitButton>
        </>
      );
    }
  };

  const error = isQuestionsError || isSubmitAnswersError;
  if (error) {
    return <ErrorBlock />;
  }

  const isLoading = isLoadingQuestions || isLoadingAdditionalQuestions;
  if (isLoading || !allQuestions.length) {
    return <Loader loadingText="Loading..." />;
  }

  if (isLoadingSubmitAnswers || isSubmitLoadingTimerActive) {
    return <ResultsLoader />;
  }

  const currentQuestion = allQuestions[currentIndex];
  return (
    <S.Wrapper>
      {areAllAwswersSet && extraContinue !== 0 ? (
          isAdditionalQuestionsError ? (
            <div className="more">
              <p>You’ve answered all the questions.</p>
              <p>Great job!</p>
              <p>Your summary is ready and waiting for you.</p>
            </div>
          ) : (
            <S.MoreWrapper>
              <S.MoreDescription>
                <S.MoreText>You're on a roll! Ready for the next one?</S.MoreText>
                <S.MoreButton
                  onClick={onLoadMoreQuestions}
                  type="button"
                  className="moreButton"
                >
                  Unlock 3 more questions
                </S.MoreButton>
              </S.MoreDescription>
              <S.ResultsButton onClick={onSendResults}>
                <span className="buttonContent">See results</span>
              </S.ResultsButton>
            </S.MoreWrapper>
          )
      ) : (
        <S.QuestionContent>
          {stepsContent}
          <S.QuestionText>{currentQuestion.questionText}</S.QuestionText>
          <S.QuestionAnswersAndPic>
            <S.QuestionImage src={imageToShow} alt="question image"/>
            <S.QuestionAnswers>
              {currentQuestion.options.map(
                ({ optionId, optionText }, index) => (
                  <S.QuestionAnswer key={optionId}>
                    <S.AnswerButton
                      isActive={answers.some(
                        (answer) =>
                          answer.questionId === currentQuestion.questionId &&
                          answer.optionId === optionId,
                      )}
                      onClick={() =>
                        handleOptionClick(currentQuestion.questionId, optionId)
                      }
                    >
                      <S.AnswerNumber>{index+1}</S.AnswerNumber>
                      <S.AnswerText>{optionText}</S.AnswerText>
                    </S.AnswerButton>
                  </S.QuestionAnswer>
                ),
              )}
            </S.QuestionAnswers>

          </S.QuestionAnswersAndPic>
        </S.QuestionContent>
      )}
      {!(areAllAwswersSet && extraContinue !== 0) && (<S.BottomButtonSection>{renderBottomButton()}</S.BottomButtonSection>)}
    </S.Wrapper>
  );
};
